import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { CONCEPTS, isConceptId, type Concept, type ConceptId } from "@/concepts";
import type { Appearance } from "@/concepts/types";
import { useStored } from "@/lib/storage";

interface ConceptCtx {
  concept: Concept;
  setConcept: (id: ConceptId) => void;
  /** Explicit user choice, or null when following the concept default. */
  appearance: Appearance;
  setAppearance: (a: Appearance) => void;
  dark: boolean;
}

const Ctx = createContext<ConceptCtx | null>(null);

function urlConcept(): ConceptId | null {
  const fromSearch = new URLSearchParams(window.location.search).get("concept");
  if (isConceptId(fromSearch)) return fromSearch;
  const hashQuery = window.location.hash.split("?")[1];
  const fromHash = hashQuery ? new URLSearchParams(hashQuery).get("concept") : null;
  return isConceptId(fromHash) ? fromHash : null;
}

function useSystemDark() {
  const [dark, setDark] = useState(() => window.matchMedia("(prefers-color-scheme: dark)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const on = () => setDark(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return dark;
}

export function ConceptProvider({ children }: { children: ReactNode }) {
  const [stored, setStored] = useStored<ConceptId | null>("concept", null);
  const [appearanceChoice, setAppearanceChoice] = useStored<Appearance | null>("appearance", null);
  const systemDark = useSystemDark();

  useEffect(() => {
    const fromUrl = urlConcept();
    if (fromUrl) setStored(fromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const concept = CONCEPTS[isConceptId(stored) ? stored : "paper"];
  const appearance = appearanceChoice ?? concept.defaultAppearance;
  const dark = appearance === "dark" || (appearance === "system" && systemDark);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", dark);
  }, [dark]);

  useEffect(() => {
    document.documentElement.dataset.concept = concept.id;
  }, [concept.id]);

  const value = useMemo<ConceptCtx>(
    () => ({ concept, setConcept: (id) => setStored(id), appearance, setAppearance: (a) => setAppearanceChoice(a), dark }),
    [concept, appearance, dark, setStored, setAppearanceChoice],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useConcept(): ConceptCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error("useConcept outside ConceptProvider");
  return v;
}

/**
 * Swaps the concept's presentation for a subtree (e.g. Paper's Compact view mode renders Console's
 * dense rows and detail panel inside the Paper shell). Appearance and the app root stay as they are.
 */
export function ConceptOverride({ concept, children }: { concept: Concept; children: ReactNode }) {
  const parent = useConcept();
  const value = useMemo<ConceptCtx>(() => ({ ...parent, concept }), [parent, concept]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
